import type { BADGE_FALLBACK } from '@uefadigital/panenka';
import { PkBadge } from '@uefadigital/panenka-react';
import { memo, useMemo } from 'react';

import { hydrateWithTranslations } from '@services/TranslatorService';
import type { RoundPhase, Team } from '@shared-types/match.types';
import { TeamType } from '@shared-types/match.types';
import { isNational, isRegions, isFake, isNationsLeague } from '@utilities/live';
import { generateRandomNumber } from '@utilities/random';

export type TeamBadgeProps = {
  team?: Team;
  className?: string;
  competitionId?: string;
  phase?: RoundPhase;
  format?: string;
  size?: number;
  slot?: string;
  placeholder?: string;
};

const TeamBadge = ({
  team,
  className,
  competitionId = window.competitionId,
  phase = window.currentPhase,
  format,
  size = 32,
  slot = '',
  placeholder = null,
}: TeamBadgeProps) => {
  const isTeam =
    isNational(team) ||
    isRegions(competitionId) ||
    (team?.typeTeam?.toUpperCase() === TeamType.PLACEHOLDER && !window.isClub);

  const imgSrcSet = useMemo(() => {
    if (!team || isFake(team)) return placeholder;

    const logoSize = isNationsLeague(competitionId, phase) ? 'unl' : format;

    return logoSize ? team.logoUrl?.replace(/\/\d+x\d+\//, `/${logoSize}/`) : team.logoUrl;
  }, [competitionId, format, phase, placeholder, team]);

  const fallbackImage: BADGE_FALLBACK = useMemo(() => {
    if (isNationsLeague(competitionId, phase)) return 'unl-generic-badge';
    if (isTeam) return `ntc-generic-badge-0${generateRandomNumber(1, 4)}` as BADGE_FALLBACK;
    return 'club-generic-badge';
  }, [competitionId, isTeam, phase]);

  return (
    <PkBadge
      className={className}
      slot={slot}
      fallbackImage={fallbackImage}
      size={size}
      src={imgSrcSet}
      title={hydrateWithTranslations(team)?.displayName}
      alt={hydrateWithTranslations(team)?.displayName}
    />
  );
};

export default memo(TeamBadge);
